// Renders the long-form sections in src/data/designerLibrary.js and
// src/data/consultantContent.js: paragraphs, lists, tables, tips, links, and
// two special blocks: { t: 'tool', id } (a tool passed in by the page) and
// { t: 'sample' } (the sample heat load report).
//
// scripts/prerender.js renders the same sections as plain HTML for Google
// (longFormHtml), so what crawlers read is what people read.

import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import SampleReport from './SampleReport';

function Cta({ b }) {
  const inner = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-gray-900">{b.label}</span>
        {b.text && <span className="mt-0.5 block text-xs leading-relaxed text-gray-600">{b.text}</span>}
      </span>
      <span aria-hidden="true" className="text-lg text-blue-700">›</span>
    </>
  );
  const cls = 'my-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3.5 hover:border-blue-400';
  return b.href.startsWith('http')
    ? <a href={b.href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
    : <Link to={b.href} className={cls}>{inner}</Link>;
}

function Block({ b, tools }) {
  switch (b.t) {
    case 'p':
      return <p className="my-3 text-[15px] leading-relaxed text-gray-700">{b.text}</p>;
    case 'ul':
      return (
        <ul className="my-3 flex flex-col gap-2">
          {b.items.map((x) => (
            <li key={x} className="flex gap-2 text-[15px] leading-relaxed text-gray-700">
              <Check className="mt-1 h-4 w-4 shrink-0 text-blue-700" strokeWidth={2.6} aria-hidden="true" />
              <span>{x}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="my-3 flex flex-col gap-3">
          {b.items.map((x, i) => (
            <li key={x} className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[13px] font-extrabold text-blue-700">{i + 1}</span>
              <span className="pt-0.5 text-[15px] leading-relaxed text-gray-700">{x}</span>
            </li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div className="my-4">
          {/* Phones: each row as a card, so no column is cut off. */}
          <div className="flex flex-col gap-2 sm:hidden">
            {b.rows.map((r) => (
              <div key={r.join('|')} className="rounded-xl border border-gray-200 p-3">
                <p className="text-sm font-bold text-gray-900">{r[0]}</p>
                <dl className="mt-1.5 flex flex-col gap-1">
                  {r.slice(1).map((c, i) => (
                    <div key={b.head[i + 1]} className="grid grid-cols-[110px_1fr] gap-2 text-[13px] leading-snug">
                      <dt className="text-gray-500">{b.head[i + 1]}</dt>
                      <dd className="text-gray-800">{c}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 sm:block">
            <table className="w-full min-w-[520px] border-collapse text-left text-[13px] sm:text-sm">
              <thead className="bg-gray-50">
                <tr>{b.head.map((h) => <th key={h} scope="col" className="px-3 py-2.5 font-semibold text-gray-900">{h}</th>)}</tr>
              </thead>
              <tbody>
                {b.rows.map((r) => (
                  <tr key={r.join('|')} className="border-t border-gray-100 align-top">
                    {r.map((c, i) => (
                      i === 0
                        ? <th key={c} scope="row" className="px-3 py-2.5 font-semibold text-gray-900">{c}</th>
                        : <td key={c + i} className="px-3 py-2.5 leading-relaxed text-gray-700">{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {b.note && <p className="mt-2 text-xs leading-relaxed text-gray-500">{b.note}</p>}
        </div>
      );
    case 'tip':
      return (
        <div className="my-4 rounded-xl bg-amber-50 p-4">
          <p className="text-sm font-bold text-amber-900">{b.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-900">{b.text}</p>
        </div>
      );
    case 'cta':
      return <Cta b={b} />;
    case 'tool':
      return tools && tools[b.id] ? <div className="my-5">{tools[b.id]}</div> : null;
    case 'sample':
      return <div className="my-5"><SampleReport /></div>;
    default:
      return null;
  }
}

export default function LongForm({ sections, tools, folded = false, headingClass = 'text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl' }) {
  // Folded: each section is a closed panel the reader opens. The text is
  // still on the page (and in the pre-render) for search.
  if (folded) {
    return (
      <div className="divide-y divide-gray-200 overflow-hidden rounded-2xl border border-gray-200">
        {sections.map((s) => (
          <details key={s.id} id={s.id} className="group scroll-mt-36">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-4 text-left [&::-webkit-details-marker]:hidden">
              <span className="text-[16px] font-bold text-gray-900">{s.title}</span>
              <svg viewBox="0 0 20 20" className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </summary>
            <div className="px-4 pb-5">
              {s.blocks.map((b, i) => <Block key={`${s.id}-${i}`} b={b} tools={tools} />)}
            </div>
          </details>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-10">
      {sections.map((s) => (
        <section key={s.id} id={s.id} className="scroll-mt-28">
          <h2 className={headingClass}>{s.title}</h2>
          {s.blocks.map((b, i) => <Block key={`${s.id}-${i}`} b={b} tools={tools} />)}
        </section>
      ))}
    </div>
  );
}
